type ColorScheme = 'light' | 'dark'
type RandomColorTone = 'bg' | 'bgStrong' | 'border' | 'text' | 'textStrong'

export type RandomColor = {
    bgLight: string
    bgDark: string
    bgStrongLight: string
    bgStrongDark: string
    borderLight: string
    borderDark: string
    textLight: string
    textDark: string
    textStrongLight: string
    textStrongDark: string
}

const getSeedHash = (seed: string) => {
    let hash = 2166136261

    for (const char of seed) {
        hash ^= char.charCodeAt(0)
        hash = Math.imul(hash, 16777619)
    }

    return hash >>> 0
}

const getUnitFromHash = (hash: number) => {
    return hash / 0xffffffff
}

const getByteUnitFromHash = (hash: number, shift: 0 | 8 | 16 | 24) => {
    return ((hash >>> shift) & 0xff) / 0xff
}

export const getRandomColor = (seed: string): RandomColor => {
    // Turn the input string into a stable hash.
    // Same seed always gives the same palette.
    const hash = getSeedHash(seed.toLowerCase())

    // Pick the base color family: red / blue / green / etc.
    const hue = getUnitFromHash(hash) * 360

    // These are just three independent 0..1 values from different parts of the hash.
    // They do NOT pick different hues.
    // They only control how soft / vivid / strong each role should be.
    const backgroundSoftness = getByteUnitFromHash(hash, 8)
    const textIntensity = getByteUnitFromHash(hash, 16)
    const accentStrength = getByteUnitFromHash(hash, 24)

    // Backgrounds stay low-chroma so they feel subtle.
    const lightBackgroundChroma = 0.028 + backgroundSoftness * 0.018

    // Use the more vivid accent profile as the default strong color in light mode.
    const strongBackgroundLightChroma = 0.22 + accentStrength * 0.09

    // Dark mode keeps the same family but pushes it darker.
    const strongBackgroundDarkChroma = strongBackgroundLightChroma

    // Light-mode strong background brightness.
    const strongBackgroundLightness = 0.7 - backgroundSoftness * 0.06

    // Dark-mode strong background brightness.
    const strongDarkBackgroundLightness = Math.max(0.48, strongBackgroundLightness - 0.2)

    // Text gets a bit more chroma so it doesn't look too dull.
    const textChroma = 0.088 + textIntensity * 0.024

    return {
        // Soft background colors
        bgLight: `oklch(0.94 ${lightBackgroundChroma} ${hue})`,
        bgDark: `oklch(0.28 0.06 ${hue})`,

        // Strong/accent background colors
        bgStrongLight: `oklch(${strongBackgroundLightness} ${strongBackgroundLightChroma} ${hue})`,
        bgStrongDark: `oklch(${strongDarkBackgroundLightness} ${strongBackgroundDarkChroma} ${hue})`,

        // Borders: same hue, slightly more visible than backgrounds
        borderLight: `oklch(0.83 ${lightBackgroundChroma + 0.02} ${hue})`,
        borderDark: `oklch(0.5 ${textChroma} ${hue})`,

        // Text: same hue, tuned for readability
        textLight: `oklch(0.38 ${textChroma} ${hue})`,
        textDark: `oklch(0.82 0.08 ${hue})`,
        textStrongLight: `oklch(0.42 ${textChroma * 1.4} ${hue})`,
        textStrongDark: `oklch(0.82 0.12 ${hue})`,
    }
}

export const getRandomColorValue = (color: RandomColor, colorScheme: ColorScheme, tone: RandomColorTone) => {
    if (tone === 'bg') {
        return colorScheme === 'dark' ? color.bgDark : color.bgLight
    }

    if (tone === 'bgStrong') {
        return colorScheme === 'dark' ? color.bgStrongDark : color.bgStrongLight
    }

    if (tone === 'border') {
        return colorScheme === 'dark' ? color.borderDark : color.borderLight
    }

    if (tone === 'textStrong') {
        return colorScheme === 'dark' ? color.textStrongDark : color.textStrongLight
    }

    return colorScheme === 'dark' ? color.textDark : color.textLight
}

export const getRandomColorStyle = (color: RandomColor, colorScheme: ColorScheme, mode: 'light' | 'solid') => {
    if (mode === 'solid') {
        return {
            backgroundColor: getRandomColorValue(color, colorScheme, 'text'),
            borderColor: getRandomColorValue(color, colorScheme, 'border'),
            color: getRandomColorValue(color, colorScheme, 'bg'),
        }
    }

    return {
        backgroundColor: getRandomColorValue(color, colorScheme, 'bg'),
        borderColor: getRandomColorValue(color, colorScheme, 'border'),
        color: getRandomColorValue(color, colorScheme, 'text'),
    }
}
